import classNames from "classnames";
import classes from "./SearchResultFCC.module.less";
import { Card } from "~/Configurators/Linear/components/Card";
import { ProductDescriptorContext } from "~/feature/Product/ProductContext";
import { useAddToCartForm } from "~/AddToCart/hooks/useAddToCartNew";
import { makeSalesAwarePackCodeProvider } from "~/SalesCampaign/salesAwarePackCodeProvider";
import { useTranslation } from "react-i18next";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";
import { Product } from "~/feature/Product";
import { SchaefflerIdParameter } from "~/common/components/Product/ProductParams";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { MAX_QUANTITY } from "~/utils/constants";
import { canSeePriceRefColumn } from "~/QuickEntryPopup/helpers";
import { PriceReferenceParam } from "~/feature/cart/cart/components/LineItemEditableParams";
import { BasicAddToWishlistButton } from "~/feature/Product/elements/AddToWishlistButton";
import { AddToCartButtonBase } from "~/feature/Product/elements/AddToCartButton";
import { canSeeAddToCartButton, canSeeWishlistButton } from "~/utils/features";
import { FCCTag } from "~/FreeCapacityPush/components/FCCTag/FCCTag";
import { LinkButton } from "~/common/components/Link";
import { IconType } from "~/common/components/icon/IconType";
import { Icon } from "~/common/components/icon/Icon";
import { Collapse } from "~/common/components/Collapse";
import { useState } from "react";
import Parameter from "~/common/components/Parameter";
import { addToCartApi } from "~/AddToCart/redux/addToCart/actions";
import { getCanUserAddToCart } from "~/ProductDetails/components/ProductSummary/ExtendedPdpSummary";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { Price as PriceV3 } from "~/common/components/Price/v3/Price";
import { ButtonBase, IconButton } from "@mui/material";
import { produce } from "immer";
import { FccAction, trackFccAction } from "~/FreeCapacityPush/api/trackFccAction";
import { Alert } from "~/common/m3/Alert/Alert";
import { AnalyticsMQ } from "~/feature/Analytics/AnalyticsMQ/AnalyticsMQ";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";

interface Props {
  className?: string;
  productCode: string;
  isSelected?: boolean;
  onSelect?: (productCode: string | null, fifteenDigits: string | null) => void;
  searchQuery: string;
}

export const SearchResultFCC = ({
  className,
  productCode,
  searchQuery,
  onSelect: onSelectExternal,
  isSelected,
}: Props) => {
  const { t } = useTranslation();
  const [packageSelectionOpen, setPackageSelectionOpen] = useState(false);

  const [formProps, productDescriptor] = useAddToCartForm({
    productCode,
    // initialPackCodeProvider: makeSalesAwarePackCodeProvider(null, queryBasedPackCodeProvider(searchQuery)),
    initialPackCodeProvider: (productDescriptor, quantity) => {
      const alteredProductDescriptor = produce(productDescriptor, (draft) => {
        const fccPackages = getFccPackages(productDescriptor);
        if (draft.availability) {
          draft.availability.packagingInformation = fccPackages;
        }
      });

      const preselectEntries = Object.entries(productDescriptor.base.fifteenDigit)
        .filter(([packCode, entry]) => entry.preselect)
        .map(([packCode, _]) => packCode);
      const preselectedPackageEntry = alteredProductDescriptor.availability?.packagingInformation?.filter((pack) =>
        preselectEntries.includes(pack.packCode),
      )[0];
      if (preselectedPackageEntry) {
        return preselectedPackageEntry.packCode;
      }

      return makeSalesAwarePackCodeProvider(null)(alteredProductDescriptor, quantity);
    },
  });

  const selectedPack = formProps.watch("selectedPack");
  const onSelectPackage = (packCode: string) => {
    formProps.setValue("selectedPackCode", packCode);
    const fifteenDigits = productDescriptor.base.sapMaterialNumber + packCode;
    onSelectExternal?.(productCode, fifteenDigits);
  };
  const packCode = formProps.watch("selectedPackCode");
  const packSize = selectedPack?.packSize ?? 1;

  const quantity = formProps.watch("quantity");

  let isPackageSizeInvalid = false;
  if (selectedPack) {
    isPackageSizeInvalid = quantity === 0 || quantity % selectedPack.packSize > 0;
  }

  const priceReference = formProps.watch("priceReference");

  const addToWishlistAvailable = canSeeWishlistButton();
  const addToCartAvailable = canSeeAddToCartButton();

  const getPackagingLabel = (packCode: string, packSize: number) => {
    return t("addToCartPopup.packSize", {
      amount: packSize,
      code: packCode,
    });
  };

  const wishlistWizardProps = {
    productInput: productDescriptor.base,
    initialQuantity: formProps.watch("quantity"),
    initialPriceReference: formProps.watch("priceReference"),
  };

  const userCanAddToCart = getCanUserAddToCart(productDescriptor, selectedPack);

  const webservicesDisabled = window.SCHA.config.disableAllWS;
  const addToCartDisabled =
    webservicesDisabled ||
    !userCanAddToCart ||
    isPackageSizeInvalid ||
    productDescriptor.isLoading ||
    productDescriptor.isError;

  const doAddToCart = () => {
    const formValues = formProps.getValues();
    addToCartApi({
      restrictionQuery: "",
      productCode,
      ...formValues,
      addToCartSource: "fcc-addToCart",
    });
  };

  const price = formProps.watch("netPrice");
  const regularPriceValid = price && price.value > 0;

  const fifteenDigits = productDescriptor.base.sapMaterialNumber + packCode;

  const onSelect = (newValue: boolean) => {
    if (newValue) {
      trackFccAction({
        actionType: FccAction.SelectProduct,
        actionContent: fifteenDigits,
      });
    }
    onSelectExternal?.(newValue ? productCode : null, newValue ? fifteenDigits : null);
  };

  const fccPackages = getFccPackages(productDescriptor);
  const hasMultiplePackages = fccPackages.length > 1;

  return (
    <ProductDescriptorContext value={productDescriptor}>
      <Card className={classNames(classes.root, className)} data-test-id={`search-result-item`} active={isSelected}>
        <div className={classes.content}>
          <IconButton
            onClick={() => onSelect(!isSelected)}
            className={classes.selectButton}
            disabled={productDescriptor.isLoading || productDescriptor.isError}
          >
            <CheckedCircle checked={isSelected} alwaysVisible={true} />
          </IconButton>
          <Product.PrimaryImageWithLogos linkProps={{ target: "_blank" }} />
          <div className={classes.dataSection}>
            <Stacker gap={16}>
              <Stacker gap={8}>
                <div className={classes.designation}>
                  {productDescriptor.base.preferred && <Product.PreferredIndicator className={"icon-margin"} />}
                  <Product.Designation linkProps={{ target: "_blank" }} />
                </div>
                <Product.ShortDescription clickable={false} />
              </Stacker>
              <SchaefflerIdParameter variant={"horizontal"} />
            </Stacker>
            <div className={classes.netPriceSection}>
              {productDescriptor.isLoading && (
                <div className={"mb-30"}>
                  <CircleSpinner />
                </div>
              )}
              {productDescriptor.isLoaded && (
                <>
                  {!regularPriceValid && true && (
                    <span
                      className={classNames({
                        [classes.regularPrice]: true,
                        [classes.priceOnRequest]: true,
                        [classes.mainPrice]: true,
                      })}
                    >
                      {t("product.priceOnRequest")}
                    </span>
                  )}
                  {regularPriceValid && (
                    <PriceV3
                      className={classNames({
                        [classes.regularPrice]: true,
                        [classes.mainPrice]: true,
                        [classes.active]: true,
                      })}
                      price={price}
                    />
                  )}
                  {(regularPriceValid || false) && (
                    <div className={classes.priceLabels}>
                      <PriceV3.NetPriceUnit price={price} surchargeEnabled={false} />
                    </div>
                  )}
                </>
              )}
            </div>
            <QuantityInput
              min={packSize}
              max={MAX_QUANTITY}
              value={quantity}
              disabled={productDescriptor.isLoading || !productDescriptor.base.quantityVisible}
              step={packSize}
              data-test-id={"quantity-input"}
              onValueChange={(value) => {
                formProps.setValue("quantity", value, { shouldTouch: true, shouldDirty: true });
                onSelectExternal?.(productCode, fifteenDigits);
              }}
              error={isPackageSizeInvalid}
            />
            <PriceReferenceParam
              product={productDescriptor.base}
              priceReference={priceReference}
              data-test-id={"price-reference"}
              onChange={(value) => {
                formProps.setValue("priceReference", value ?? "");
                onSelectExternal?.(productCode, fifteenDigits);
              }}
              editable={canSeePriceRefColumn()}
            />
            <div className={classes.actionSection}>
              <div className={classes.packaging}>
                {productDescriptor.isLoading && <CircleSpinner />}
                {productDescriptor.isLoaded && (
                  <>
                    <span>{getPackagingLabel(packCode, packSize)}</span>
                    {hasMultiplePackages && (
                      <LinkButton
                        onClick={() => setPackageSelectionOpen((prev) => !prev)}
                        iconBefore={<Icon type={packageSelectionOpen ? IconType.ChevronUp : IconType.ChevronDown} />}
                      >
                        {t("page.fcc.product.changePackage")}
                      </LinkButton>
                    )}
                  </>
                )}
                {productDescriptor.isError && <Alert severity={"error"}>{t("hybrisAjaxError")}</Alert>}
              </div>
              <div className={classes.buttons}>
                {addToWishlistAvailable && (
                  <BasicAddToWishlistButton
                    size={"medium"}
                    sideButton={true}
                    color={"secondary"}
                    wizardProps={wishlistWizardProps}
                    onClick={() => {
                      const formValues = formProps.getValues();
                      trackFccAction({
                        actionType: FccAction.AddToWishlist,
                        actionContent: fifteenDigits,
                        netPrice: formValues.netPrice?.value,
                        quantity: formValues.quantity,
                        priceUnit: formValues.priceUnit,
                      });
                    }}
                  />
                )}
                {addToCartAvailable && (
                  <AddToCartButtonBase
                    size={"medium"}
                    className={classes.cartBtn}
                    actionVariant={"salesman"}
                    disabled={addToCartDisabled}
                    onClick={doAddToCart}
                  />
                )}
              </div>
            </div>
          </div>
          <div className={classes.tagSection}>
            <FCCTag anchor={"left"} />
          </div>
        </div>
        <Collapse in={packageSelectionOpen}>
          <div className={classes.packagingContent}>
            {fccPackages.map((packaging) => (
              <Card
                as={ButtonBase}
                className={classes.packagingItem}
                onClick={() => onSelectPackage(packaging.packCode)}
              >
                <CheckedCircle checked={packCode === packaging.packCode} alwaysVisible={true} />
                <Parameter
                  label={t("availabilityTable.packagingCode")}
                  value={getPackagingLabel(packaging.packCode, packaging.packSize)}
                />
              </Card>
            ))}
          </div>
        </Collapse>
      </Card>
    </ProductDescriptorContext>
  );
};

function getFccPackages(productDescriptor: ProductDescriptor) {
  const packages = productDescriptor.availability?.packagingInformation;
  if (packages) {
    const fccPackageCodes = Object.entries(productDescriptor.base.fifteenDigit)
      .filter(([_, entry]) => entry.isFcc)
      .map(([packCode, _]) => packCode);

    return packages.filter((pack) => fccPackageCodes.includes(pack.packCode));
  }

  return [];
}

AnalyticsMQ.instance.subscribe("addToCart", ({ products, context: { source } }) => {
  if (source !== "fcc-addToCart") {
    return;
  }

  (Array.isArray(products) ? products : [products]).forEach(({ product, packaging, quantity, price }) => {
    const fifteenDigits = product.sapMaterialNumber + packaging.code;
    trackFccAction({
      actionType: FccAction.AddToQuote,
      actionContent: fifteenDigits,
      netPrice: price?.value,
      quantity: quantity,
      priceUnit: price?.unit ?? 1,
    });
  });
});
